/**
 * SliderExamples.tsx
 * Comprehensive examples of HeroUI Slider component
 * 
 * Showcases all HeroUI Slider features using our shared components
 */

import React, { useState } from 'react';
import { PlaygroundPageLayout } from '../shared';
import { Slider } from '@enfomentis/ui';
import { Card, CardHeader, CardBody } from '@enfomentis/ui';
import { Badge } from '@enfomentis/ui';
import { 
  Volume2,
  Thermometer,
  Zap,
  Settings
} from 'lucide-react';

export const SliderExamples: React.FC = () => {
  // Individual slider states
  const [sliderStates, setSliderStates] = useState<Record<string, number | number[]>>({
    basic: 50,
    temperature: 22,
    volume: 75,
    brightness: 80,
    range: [20, 80],
    priceRange: [100, 500],
    vertical: 60,
    steps: 5,
    precision: 0.7,
    percentage: 65
  });

  const updateSliderState = (key: string) => (value: number | number[]) => {
    setSliderStates(prev => ({ ...prev, [key]: value }));
  };

  const getSliderState = (key: string): number | number[] => {
    return sliderStates[key] ?? 0;
  };

  const sections = [
    {
      title: "Basic Usage",
      description: "Simple sliders with different configurations",
      examples: [
        {
          title: "Default Slider",
          component: (
            <div className="space-y-4 w-full">
              <Slider 
                label="Basic Slider"
                value={getSliderState('basic') as number}
                onChange={updateSliderState('basic')}
                className="max-w-md"
              />
              <p className="text-sm text-text-secondary">Value: {getSliderState('basic')}</p>
            </div>
          ),
          code: `<Slider 
  label="Basic Slider"
  value={value}
  onChange={setValue}
/>`
        },
        {
          title: "With Min/Max/Step",
          component: (
            <div className="space-y-4 w-full">
              <Slider 
                label="Temperature (°C)"
                value={getSliderState('temperature') as number}
                onChange={updateSliderState('temperature')}
                minValue={-10}
                maxValue={40}
                step={0.5}
                className="max-w-md"
              />
              <p className="text-sm text-text-secondary">Temperature: {getSliderState('temperature')}°C</p>
            </div>
          ),
          code: `<Slider 
  label="Temperature (°C)"
  value={temperature}
  onChange={setTemperature}
  minValue={-10}
  maxValue={40}
  step={0.5}
/>`
        },
        {
          title: "Disabled Slider",
          component: (
            <div className="space-y-4 w-full">
              <Slider 
                label="Disabled Setting"
                defaultValue={30}
                isDisabled
                className="max-w-md"
              />
              <p className="text-sm text-text-secondary">This slider is disabled</p>
            </div>
          ),
          code: `<Slider 
  label="Disabled Setting"
  defaultValue={30}
  isDisabled
/>`
        }
      ]
    },
    {
      title: "Colors",
      description: "All available color variants with theme integration",
      examples: [
        {
          title: "Color Variants",
          component: (
            <div className="space-y-6 w-full">
              <Slider 
                label="Primary"
                color="primary" 
                defaultValue={60}
                className="max-w-md"
              />
              <Slider 
                label="Secondary"
                color="secondary" 
                defaultValue={70}
                className="max-w-md"
              />
              <Slider 
                label="Success"
                color="success" 
                defaultValue={80}
                className="max-w-md"
              />
              <Slider 
                label="Warning"
                color="warning" 
                defaultValue={40}
                className="max-w-md"
              />
              <Slider 
                label="Danger"
                color="danger" 
                defaultValue={20}
                className="max-w-md"
              />
            </div>
          ),
          code: `<Slider color="primary" defaultValue={60} />
<Slider color="secondary" defaultValue={70} />
<Slider color="success" defaultValue={80} />
<Slider color="warning" defaultValue={40} />
<Slider color="danger" defaultValue={20} />`
        }
      ]
    },
    {
      title: "Sizes",
      description: "Different size variants",
      examples: [
        {
          title: "Size Variants",
          component: (
            <div className="space-y-6 w-full">
              <Slider 
                label="Small"
                size="sm" 
                color="primary"
                defaultValue={30}
                className="max-w-md"
              />
              <Slider 
                label="Medium (default)"
                size="md" 
                color="primary"
                defaultValue={50}
                className="max-w-md"
              />
              <Slider 
                label="Large"
                size="lg" 
                color="primary"
                defaultValue={70}
                className="max-w-md"
              />
            </div>
          ),
          code: `<Slider size="sm" color="primary" defaultValue={30} />
<Slider size="md" color="primary" defaultValue={50} />
<Slider size="lg" color="primary" defaultValue={70} />`
        }
      ]
    },
    {
      title: "Range Sliders",
      description: "Sliders with multiple values for range selection",
      examples: [
        {
          title: "Range Selection",
          component: (
            <div className="space-y-4 w-full">
              <Slider 
                label="Value Range"
                value={getSliderState('range') as number[]}
                onChange={updateSliderState('range')}
                color="primary"
                className="max-w-md"
              />
              <p className="text-sm text-text-secondary">
                Range: {(getSliderState('range') as number[])[0]} - {(getSliderState('range') as number[])[1]}
              </p>
            </div>
          ),
          code: `<Slider 
  label="Value Range"
  value={[20, 80]}
  onChange={setRange}
  color="primary"
/>`
        },
        {
          title: "Price Range",
          component: (
            <div className="space-y-4 w-full">
              <Slider 
                label="Price Range ($)"
                value={getSliderState('priceRange') as number[]}
                onChange={updateSliderState('priceRange')}
                minValue={0}
                maxValue={1000}
                step={10}
                color="success"
                className="max-w-md"
              />
              <p className="text-sm text-text-secondary">
                Price: ${(getSliderState('priceRange') as number[])[0]} - ${(getSliderState('priceRange') as number[])[1]}
              </p>
            </div>
          ),
          code: `<Slider 
  label="Price Range ($)"
  value={priceRange}
  onChange={setPriceRange}
  minValue={0}
  maxValue={1000}
  step={10}
/>`
        }
      ]
    },
    {
      title: "Orientations",
      description: "Horizontal and vertical slider orientations",
      examples: [
        {
          title: "Horizontal (Default)",
          component: (
            <div className="space-y-4 w-full">
              <Slider 
                label="Horizontal Slider"
                orientation="horizontal"
                defaultValue={65}
                color="secondary"
                className="max-w-md"
              />
              <p className="text-sm text-text-secondary">Standard horizontal orientation</p>
            </div>
          ),
          code: `<Slider 
  label="Horizontal Slider"
  orientation="horizontal"
  defaultValue={65}
/>`
        },
        {
          title: "Vertical",
          component: (
            <div className="space-y-4 w-full flex flex-col items-center">
              <div className="h-48 flex items-center">
                <Slider 
                  label="Volume"
                  orientation="vertical"
                  value={getSliderState('vertical') as number}
                  onChange={updateSliderState('vertical')}
                  color="primary"
                />
              </div>
              <p className="text-sm text-text-secondary">Volume: {getSliderState('vertical')}%</p>
            </div>
          ),
          code: `<Slider 
  label="Volume"
  orientation="vertical"
  value={volume}
  onChange={setVolume}
/>`
        }
      ]
    },
    {
      title: "Step Values & Precision",
      description: "Custom step increments and decimal precision",
      examples: [
        {
          title: "Integer Steps",
          component: (
            <div className="space-y-4 w-full">
              <Slider 
                label="Rating (1-10)"
                value={getSliderState('steps') as number}
                onChange={updateSliderState('steps')}
                minValue={1}
                maxValue={10}
                step={1}
                color="warning"
                className="max-w-md"
              />
              <p className="text-sm text-text-secondary">Rating: {getSliderState('steps')}/10</p>
            </div>
          ),
          code: `<Slider 
  label="Rating (1-10)"
  value={rating}
  onChange={setRating}
  minValue={1}
  maxValue={10}
  step={1}
/>`
        },
        {
          title: "Decimal Precision",
          component: (
            <div className="space-y-4 w-full">
              <Slider 
                label="Precision Value"
                value={getSliderState('precision') as number}
                onChange={updateSliderState('precision')}
                minValue={0}
                maxValue={1}
                step={0.01}
                color="danger"
                className="max-w-md"
              />
              <p className="text-sm text-text-secondary">Value: {(getSliderState('precision') as number).toFixed(2)}</p>
            </div>
          ),
          code: `<Slider 
  label="Precision Value"
  value={value}
  onChange={setValue}
  minValue={0}
  maxValue={1}
  step={0.01}
/>`
        }
      ]
    },
    {
      title: "Dashboard Example",
      description: "Real-world usage in a control dashboard",
      examples: [
        {
          title: "System Controls",
          component: (
            <Card className="max-w-md">
              <CardHeader>
                <h3 className="text-lg font-semibold">System Settings</h3>
              </CardHeader>
              <CardBody className="space-y-6">
                <div className="flex items-center gap-4">
                  <Volume2 className="w-5 h-5 text-primary" />
                  <div className="flex-1">
                    <Slider 
                      label="Volume"
                      value={getSliderState('volume') as number}
                      onChange={updateSliderState('volume')}
                      minValue={0}
                      maxValue={100}
                      color="primary"
                      className="w-full"
                    />
                    <p className="text-xs text-text-secondary mt-1">{getSliderState('volume')}%</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <Zap className="w-5 h-5 text-warning" />
                  <div className="flex-1">
                    <Slider 
                      label="Brightness"
                      value={getSliderState('brightness') as number}
                      onChange={updateSliderState('brightness')}
                      minValue={0}
                      maxValue={100}
                      color="warning"
                      className="w-full"
                    />
                    <p className="text-xs text-text-secondary mt-1">{getSliderState('brightness')}%</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <Thermometer className="w-5 h-5 text-danger" />
                  <div className="flex-1">
                    <Slider 
                      label="Temperature"
                      defaultValue={22}
                      minValue={16}
                      maxValue={30}
                      step={0.5}
                      color="danger"
                      className="w-full"
                    />
                    <p className="text-xs text-text-secondary mt-1">22°C</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4 opacity-50">
                  <Settings className="w-5 h-5 text-secondary" />
                  <div className="flex-1">
                    <Slider 
                      label="Performance Mode"
                      defaultValue={50}
                      isDisabled
                      color="secondary"
                      className="w-full"
                    />
                    <p className="text-xs text-text-secondary mt-1">Requires Pro license</p>
                  </div>
                </div>
              </CardBody>
            </Card>
          ),
          code: `<div className="flex items-center gap-4">
  <Volume2 className="w-5 h-5 text-primary" />
  <div className="flex-1">
    <Slider 
      label="Volume"
      value={volume}
      onChange={setVolume}
      minValue={0}
      maxValue={100}
      color="primary"
    />
    <p className="text-xs text-text-secondary">{volume}%</p>
  </div>
</div>`
        }
      ]
    }
  ];

  return (
    <PlaygroundPageLayout>
      <div className="space-y-8">
        
        {/* Header */}
        <div className="space-y-2">
          <h1 className="text-3xl font-bold text-text-primary">Slider Examples</h1>
          <p className="text-text-secondary">
            Comprehensive showcase of HeroUI Slider component with automatic theme integration
          </p>
        </div>

        {sections.map((section, sectionIndex) => (
          <Card key={sectionIndex}>
            <CardHeader>
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-semibold">{section.title}</h2>
                <Badge color="primary" variant="flat" size="sm">Range control</Badge>
              </div>
            </CardHeader>
            <CardBody className="space-y-6">
              <p className="text-text-secondary">{section.description}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {section.examples.map((example, exampleIndex) => (
                  <div key={exampleIndex} className="space-y-3">
                    <h3 className="font-medium text-text-primary">{example.title}</h3>
                    <div className="p-4 bg-content2 rounded-lg min-h-[120px] flex items-center justify-center">
                      {example.component}
                    </div>
                  </div>
                ))}
              </div>
            </CardBody>
          </Card>
        ))}
        
      </div>
    </PlaygroundPageLayout>
  );
};